import { Text, View } from '@react-pdf/renderer'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { chooseDBTranslation } from 'utils/translations'
import { convertToDisplayDate } from 'utils/utils'
import { useStyles } from './styles'
import {
  IProject,
  IEmployer,
  IEducation,
  ICourse,
  IPersonCertificate,
  IPersonCompetence,
  ILeisureActivity,
  IReference,
  IUrl,
} from 'types/cvsInterfaces'
import colors from 'constants/colors'
import { ICVData, ILayout } from 'types/layoutInterfaces'
import { findHideSetting, isDetailHidden } from '../hideSettings'

/**
 * Renders a data layout based on the given data, type, and heading color.
 *
 * @param {object} data - the data to be rendered
 * @param {string} type - the type of data to be rendered ('projects', 'employments', 'degrees', 'courses', 'certificates', 'references', 'urls')
 * @param {string} headingColor - the color of the heading
 * @return {React.ReactElement} the rendered data layout
 * @notExported
 */
const DataLayout: React.FC<{
  data: ICVData
  type: string
  headingColor: string
  layout?: ILayout
}> = ({ data, type, headingColor, layout }) => {
  const { t, i18n } = useTranslation()
  const styles = useStyles()
  const hideSetting = findHideSetting(layout, type)

  const hidden = useMemo(() => {
    const sectionItems = data[type as keyof ICVData] as unknown[] | undefined
    if (sectionItems && sectionItems.length) {
      return false
    } else {
      return true
    }
  }, [data, type])

  const items: React.ReactElement[] = useMemo(() => {
    switch (type) {
      case 'projects':
        return data.projects.map((item: IProject, index: number) => {
          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t(`print.projects.title`)}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>{chooseDBTranslation(i18n, item).name}</Text>
                )}
                {!isDetailHidden(hideSetting, 'date') && (
                  <Text style={styles.dateText}>
                    {convertToDisplayDate(item.startDate, item.dateResolution)} -{' '}
                    {item.endDate ? convertToDisplayDate(item.endDate, item.dateResolution) : null}
                  </Text>
                )}
              </View>
              {!isDetailHidden(hideSetting, 'primaryRole') && (
                <>
                  {chooseDBTranslation(i18n, item).primaryRole.length > 0 && (
                    <View style={styles.projectSkillsContainer}>
                      <Text style={styles.itemHeader}>{t('print.project.role')}: </Text>
                      <Text style={styles.text}>{chooseDBTranslation(i18n, item).primaryRole}</Text>
                    </View>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'projectRoles') && (
                <>
                  {item.ProjectRoles && item.ProjectRoles?.length > 0 && (
                    <View style={styles.projectSkillsContainer}>
                      <Text style={styles.itemHeader}>{t('print.project.roles')}: </Text>
                      {item.ProjectRoles.map(role => (
                        <Text key={role.id} style={styles.projectSkillItem}>
                          {chooseDBTranslation(i18n, role.Skill).name}
                        </Text>
                      ))}
                    </View>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'customerName') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.project.customer')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item)?.customerName}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'employerName') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.project.employer')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item)?.employerName}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'personSkills') && (
                <>
                  {item.PersonSkills && item.PersonSkills?.length > 0 && (
                    <View style={styles.projectSkillsContainer}>
                      <Text style={styles.itemHeader}>{t('print.project.skills')}: </Text>
                      {item.PersonSkills.map(skill => (
                        <Text key={skill.id} style={styles.projectSkillItem}>
                          {chooseDBTranslation(i18n, skill.Skill).name}
                        </Text>
                      ))}
                    </View>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'industries') && (
                <>
                  {item.Industries && item.Industries?.length > 0 && (
                    <View style={styles.projectSkillsContainer}>
                      <Text style={styles.itemHeader}>{t('print.project.industries')}: </Text>
                      {item.Industries.map(industry => (
                        <Text key={industry.id} style={styles.projectSkillItem}>
                          {chooseDBTranslation(i18n, industry.Skill).name}
                        </Text>
                      ))}
                    </View>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'employments':
        return data.employments.map((item: IEmployer, index: number) => {
          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t(`print.employments.title`)}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>{chooseDBTranslation(i18n, item).name}</Text>
                )}
                {!isDetailHidden(hideSetting, 'date') && (
                  <Text style={styles.dateText}>
                    {convertToDisplayDate(item.startDate, item.dateResolution)} -{' '}
                    {item.endDate ? convertToDisplayDate(item.endDate, item.dateResolution) : null}
                  </Text>
                )}
              </View>
              {!isDetailHidden(hideSetting, 'primaryRole') && (
                <>
                  {chooseDBTranslation(i18n, item).primaryRole.length > 0 && (
                    <View style={styles.projectSkillsContainer}>
                      <Text style={styles.itemHeader}>{t('print.project.role')}: </Text>
                      <Text style={styles.text}>{chooseDBTranslation(i18n, item).primaryRole}</Text>
                    </View>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'roles') && (
                <>
                  {item.EmployerRoles && item.EmployerRoles.length > 0 && (
                    <View style={styles.projectSkillsContainer}>
                      <Text style={styles.itemHeader}>{t('print.employment.roles')}: </Text>
                      {item.EmployerRoles.map(role => (
                        <Text key={role.id} style={styles.projectSkillItem}>
                          {chooseDBTranslation(i18n, role.Skill).name}
                        </Text>
                      ))}
                    </View>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'degrees':
        return data.degrees.map((item: IEducation, index: number) => {
          let date = ''
          if (item.startDate) {
            date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t(`print.degrees.title`)}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>{chooseDBTranslation(i18n, item).school}</Text>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Text style={styles.dateText}>{date}</Text>}
              </View>
              {!isDetailHidden(hideSetting, 'degree') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.degree')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item).degree}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'courses':
        return data.courses.map((item: ICourse, index: number) => {
          let date = ''
          if (item.startDate) {
            date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t(`print.courses.title`)}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>{chooseDBTranslation(i18n, item).course}</Text>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Text style={styles.dateText}>{date}</Text>}
              </View>
              {!isDetailHidden(hideSetting, 'organizer') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.organizer')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item).organizer}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'personCertificates':
        return data.personCertificates.map((item: IPersonCertificate, index: number) => {
          let date = ''
          if (item.issueDate) {
            date += `${convertToDisplayDate(item.issueDate, item.dateResolution)}`
          }
          let endDate = ''
          if (item.endDate) {
            endDate += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t(`print.certificates.title`)}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>
                    {item.Certificate && chooseDBTranslation(i18n, item.Certificate).certificate}
                  </Text>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Text style={styles.dateText}>{date}</Text>}
              </View>
              {!isDetailHidden(hideSetting, 'certifier') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.certifier')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item).certifier}</Text>
                </View>
              )}
              {endDate.length > 0 && layout && layout.showCertificateExpiry && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.certificateExpiry')}: </Text>
                  <Text style={styles.text}>{endDate}</Text>
                </View>
              )}
              {item.url && !isDetailHidden(hideSetting, 'url') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.url')}: </Text>
                  <Text style={styles.text}>{item.url}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'personCompetences':
        return data.personCompetences.map((item: IPersonCompetence, index: number) => {
          let date = ''
          if (item.issueDate) {
            date += `${convertToDisplayDate(item.issueDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t(`print.competences.title`)}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>
                    {item.Competence && chooseDBTranslation(i18n, item.Competence).name}
                  </Text>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Text style={styles.dateText}>{date}</Text>}
              </View>
              {!isDetailHidden(hideSetting, 'issuer') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.issuer')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item).issuer}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'references':
        return data.references.map((item: IReference, index: number) => {
          return (
            <View key={item.id}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t('print.reference.title')}
                </Text>
              ) : null}
              {!isDetailHidden(hideSetting, 'name') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.reference.referee')}: </Text>
                  <Text style={styles.text}>{`${item.firstName} ${item.lastName}`}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'company') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.reference.company')}: </Text>
                  <Text style={styles.text}>{item.company}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'letter') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).letter}</Text>
              )}
            </View>
          )
        })

      case 'leisureActivities':
        return data.leisureActivities.map((item: ILeisureActivity, index: number) => {
          let date = ''
          if (item.startDate) {
            date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t('print.leisureActivities.title')}
                </Text>
              ) : null}
              <View style={styles.flex}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Text style={styles.itemHeader}>{chooseDBTranslation(i18n, item).name}</Text>
                )}
                {!!date && !isDetailHidden(hideSetting, 'date') && <Text style={styles.dateText}>{date}</Text>}
              </View>
              {!!chooseDBTranslation(i18n, item).activityType && !isDetailHidden(hideSetting, 'activityType') && (
                <View style={styles.textContainer}>
                  <Text style={styles.itemHeader}>{t('print.leisureActivities.type')}: </Text>
                  <Text style={styles.text}>{chooseDBTranslation(i18n, item).activityType}</Text>
                </View>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
              )}
            </View>
          )
        })

      case 'urls':
        return data.urls.map((item: IUrl, index: number) => {
          return (
            <View wrap={false} key={item.id} style={styles.itemContainer}>
              {index === 0 ? (
                <Text style={[styles.title, { color: headingColor ? headingColor : colors.secondary }]}>
                  {t('print.links.title')}
                </Text>
              ) : null}
              <View style={styles.textContainer}>
                <Text style={styles.text}>{item.url}</Text>
              </View>
              <Text style={styles.description}>{chooseDBTranslation(i18n, item).description}</Text>
            </View>
          )
        })

      default:
        return []
    }
  }, [
    type,
    data.projects,
    data.employments,
    data.degrees,
    data.courses,
    data.personCertificates,
    data.references,
    data.leisureActivities,
    data.urls,
    data.personCompetences,
    styles.itemContainer,
    styles.title,
    styles.flex,
    styles.itemHeader,
    styles.dateText,
    styles.projectSkillsContainer,
    styles.text,
    styles.textContainer,
    styles.description,
    styles.projectSkillItem,
    hideSetting,
    headingColor,
    t,
    i18n,
    layout,
  ])

  if (hidden) {
    return <></>
  }

  return <View style={styles.container}>{items}</View>
}

export default DataLayout
